// Utility functions for document management

import type { DocumentType, Document } from '../types/Document';

export interface DocumentPeriod {
  /** Stable string key used as the option value in selectors, e.g. "2025", "2025-01", "2022-2025" */
  key: string;
  /** Human-readable label shown in the period selector */
  label: string;
  /** True when this period contains the current date */
  isCurrent: boolean;
  /** All documents that belong to this period. Length > 1 only when type.multi_uploads === true */
  documents: Document[];
}

/**
 * Builds a flat list of DocumentPeriod slots for a given document type.
 *
 * Rules:
 *  - recurrence = 'none': one period ("all") containing all documents.
 *  - recurrence = 'yearly' | 'monthly' | '3 yearly': current-period slot first
 *    (even when empty), then one slot per distinct historical period derived
 *    from uploaded documents, sorted newest-first.
 *  - multi = false: at most one document per period (enforced by the upload UX,
 *    not here).
 *  - multi = true: documents within a period are listed together.
 */
export const buildPeriods = (documents: Document[], type: DocumentType): DocumentPeriod[] => {
  if (type.recurrence === 'none') {
    const sorted = [...documents].sort(
      (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
    return [{ key: 'all', label: 'All Documents', isCurrent: true, documents: sorted }];
  }

  const now = new Date();
  const periodsMap = new Map<string, DocumentPeriod>();

  // Helper to derive a period key + label + isCurrent from a document's period_start/period_end
  const docToPeriodMeta = (doc: Document): { key: string; label: string; isCurrent: boolean } | null => {
    if (type.recurrence === '1_year') {
      if (!doc.period_start) return null;
      const year = new Date(doc.period_start).getFullYear();
      return { key: String(year), label: String(year), isCurrent: year === now.getFullYear() };
    }
    if (type.recurrence === '1_month') {
      if (!doc.period_start) return null;
      const d = new Date(doc.period_start);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      const label = `${d.toLocaleString('default', { month: 'short' })} ${d.getFullYear()}`;
      const isCurrent = d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
      return { key, label, isCurrent };
    }
    if (type.recurrence === '3_month') {
      if (!doc.period_start) return null;
      const d = new Date(doc.period_start);
      const quarterMonth = Math.floor(d.getMonth() / 3) * 3;
      const key = `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`;
      const label = `Q${Math.floor(d.getMonth() / 3) + 1} ${d.getFullYear()}`;
      const currentQuarterMonth = Math.floor(now.getMonth() / 3) * 3;
      const isCurrent = d.getFullYear() === now.getFullYear() && quarterMonth === currentQuarterMonth;
      return { key, label, isCurrent };
    }
    if (type.recurrence === '3_year') {
      if (!doc.period_start || !doc.period_end) return null;
      const start = new Date(doc.period_start).getFullYear();
      const end = new Date(doc.period_end).getFullYear();
      const key = `${start}-${end}`;
      const label = `${start} – ${end - 1}`;
      const isCurrent = now.getFullYear() >= start && now.getFullYear() < end;
      return { key, label, isCurrent };
    }
    return null;
  };

  // Compute the current-period key without needing a document
  const currentPeriodKey = (() => {
    if (type.recurrence === '1_year') return String(now.getFullYear());
    if (type.recurrence === '1_month')
      return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
    if (type.recurrence === '3_month') {
      const q = Math.floor(now.getMonth() / 3) + 1;
      return `${now.getFullYear()}-Q${q}`;
    }
    if (type.recurrence === '3_year') {
      const start = now.getFullYear();
      return `${start}-${start + 3}`;
    }
    return 'current';
  })();

  const currentPeriodLabel = (() => {
    if (type.recurrence === '1_year') return `${now.getFullYear()}`;
    if (type.recurrence === '1_month')
      return `${now.toLocaleString('default', { month: 'short' })} ${now.getFullYear()}`;
    if (type.recurrence === '3_month') {
      const q = Math.floor(now.getMonth() / 3) + 1;
      return `Q${q} ${now.getFullYear()}`;
    }
    if (type.recurrence === '3_year') {
      const start = now.getFullYear();
      return `${start} – ${start + 2}`;
    }
    return 'Current';
  })();

  // Always create the current-period slot first (may stay empty)
  periodsMap.set(currentPeriodKey, {
    key: currentPeriodKey,
    label: currentPeriodLabel,
    isCurrent: true,
    documents: [],
  });

  // Bucket each document into its period slot
  for (const doc of documents) {
    const meta = docToPeriodMeta(doc);
    if (!meta) {
      // Document has no period_start — bucket into current period as a fallback
      periodsMap.get(currentPeriodKey)!.documents.push(doc);
      continue;
    }
    if (!periodsMap.has(meta.key)) {
      periodsMap.set(meta.key, { ...meta, documents: [] });
    }
    periodsMap.get(meta.key)!.documents.push(doc);
  } 

  // Sort documents within each period newest-first
  Array.from(periodsMap.values()).forEach(period => {
    period.documents.sort(
      (a: Document, b: Document) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  });

  // Return current period first, then remaining periods sorted newest-first
  const current = periodsMap.get(currentPeriodKey)!;
  const historical = Array.from(periodsMap.values())
    .filter(p => p.key !== currentPeriodKey)
    .sort((a, b) => b.key.localeCompare(a.key));

  return [current, ...historical];
};

/**
 * Legacy helper: splits documents into current-period and historical buckets.
 * Used by completionCalculations.ts.
 */
export const organizeDocumentsByPeriod = (documents: Document[], type: DocumentType) => {
  if (type.recurrence === 'none') {
    return {
      currentDocument: documents[0] || null,
      historicalDocuments: documents.slice(1),
    };
  }

  const now = new Date();
  let currentDocument: Document | null = null;
  let historicalDocuments = documents;

  if (type.recurrence === '1_year') {
    const currentYear = now.getFullYear();
    currentDocument = documents.find((doc: Document) =>
      doc.period_start && new Date(doc.period_start).getFullYear() === currentYear
    ) || null;
    historicalDocuments = documents.filter((doc: Document) =>
      !doc.period_start || new Date(doc.period_start).getFullYear() !== currentYear
    );
  } else if (type.recurrence === '3_year') {
    const currentYear = now.getFullYear();
    currentDocument = documents.find((doc: Document) => {
      if (!doc.period_start || !doc.period_end) return false;
      const start = new Date(doc.period_start).getFullYear();
      const end = new Date(doc.period_end).getFullYear();
      return currentYear >= start && currentYear < end;
    }) || null;
    historicalDocuments = documents.filter((doc: Document) => {
      if (!doc.period_start || !doc.period_end) return true;
      const start = new Date(doc.period_start).getFullYear();
      const end = new Date(doc.period_end).getFullYear();
      return !(currentYear >= start && currentYear < end);
    });
  } else if (type.recurrence === '1_month') {
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();
    currentDocument = documents.find((doc: Document) => {
      if (!doc.period_start) return false;
      const d = new Date(doc.period_start);
      return d.getFullYear() === currentYear && d.getMonth() === currentMonth;
    }) || null;
    historicalDocuments = documents.filter((doc: Document) => {
      if (!doc.period_start) return true;
      const d = new Date(doc.period_start);
      return !(d.getFullYear() === currentYear && d.getMonth() === currentMonth);
    });
  } else if (type.recurrence === '3_month') {
    const currentQuarter = Math.floor(now.getMonth() / 3);
    const currentYear = now.getFullYear();
    currentDocument = documents.find((doc: Document) => {
      if (!doc.period_start) return false;
      const d = new Date(doc.period_start);
      return d.getFullYear() === currentYear && Math.floor(d.getMonth() / 3) === currentQuarter;
    }) || null;
    historicalDocuments = documents.filter((doc: Document) => {
      if (!doc.period_start) return true;
      const d = new Date(doc.period_start);
      return !(d.getFullYear() === currentYear && Math.floor(d.getMonth() / 3) === currentQuarter);
    });
  }

  return { currentDocument, historicalDocuments };
};

/**
 * Checks if a document has expired
 */
export const isDocumentExpired = (document: Document | null): boolean => {
  if (!document?.expiryDate) return false;
  const validPeriod = (document.period_start && document.period_end) ? new Date(document.period_start) < new Date() && new Date() < new Date(document.period_end) : true;
  const validExpiryDate = new Date(document.expiryDate) < new Date()
  return !validPeriod || !validExpiryDate;
};

/**
 * Formats period label for display
 */
export const formatPeriodLabel = (document: Document, type: DocumentType): string => {
  if (type.recurrence === '1_year' && document.period_start) {
    return new Date(document.period_start).getFullYear().toString();
  }
  
  if (type.recurrence === '3_year' && document.period_start && document.period_end) {
    const start = new Date(document.period_start).getFullYear();
    const end = new Date(document.period_end).getFullYear();
    return `${start} - ${end - 1}`;
  }
  
  if (type.recurrence === '1_month' && document.period_start) {
    const d = new Date(document.period_start);
    const month = d.toLocaleString('default', { month: 'short' });
    const year = d.getFullYear();
    return `${month} ${year}`;
  }

  if (type.recurrence === '3_month' && document.period_start) {
    const d = new Date(document.period_start);
    const q = Math.floor(d.getMonth() / 3) + 1;
    return `Q${q} ${d.getFullYear()}`;
  }
  
  return 'Document';
};

/**
 * Gets current period label for new uploads
 */
export const getCurrentPeriodLabel = (type: DocumentType): string => {
  const now = new Date();
  
  if (type.recurrence === '1_year') {
    return `${now.getFullYear()} (Current)`;
  }
  
  if (type.recurrence === '1_month') {
    const month = now.toLocaleString('default', { month: 'short' });
    return `${month} ${now.getFullYear()} (Current)`;
  }

  if (type.recurrence === '3_month') {
    const q = Math.floor(now.getMonth() / 3) + 1;
    return `Q${q} ${now.getFullYear()} (Current)`;
  }

  if (type.recurrence === '3_year') {
    const start = now.getFullYear();
    return `${start} – ${start + 2} (Current)`;
  }
  
  return 'Current';
};

/**
 * Generates form data for document upload
 */
export const createUploadFormData = (
  file: File,
  title: string,
  description: string,
  expiryDate: string,
  type: DocumentType,
  selectedUser: any,
  agency: any
): FormData => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('fileType', file.type);
  formData.append('title', title);
  formData.append('description', description);
  formData.append('typeID', type.id);
  
  // UserID is always required by the backend schema
  // For user documents: selectedUser is the user whose documents we're managing
  // For company documents: selectedUser is the manager who uploaded the document
  if (!selectedUser?.id) {
    throw new Error('User ID is required for document upload');
  }
  formData.append('userID', selectedUser.id);
  
  formData.append('agencyID', agency.agencyId);

  // Set document period dates based on recurrence type.
  // Use expiry date as the anchor when available, otherwise fall back to today.
  // Always use UTC methods to avoid timezone-shifting date boundaries.
  const anchor = expiryDate ? new Date(expiryDate) : new Date();
  const anchorYear = anchor.getUTCFullYear();
  const anchorMonth = anchor.getUTCMonth();

  if (type.recurrence === '1_year') {
    formData.append('period_start', new Date(Date.UTC(anchorYear, 0, 1)).toISOString());
    formData.append('period_end', new Date(Date.UTC(anchorYear + 1, 0, 1)).toISOString());
  } else if (type.recurrence === '3_year') {
    formData.append('period_start', new Date(Date.UTC(anchorYear - 2, 0, 1)).toISOString());
    formData.append('period_end', new Date(Date.UTC(anchorYear + 1, 0, 1)).toISOString());
  } else if (type.recurrence === '1_month') {
    formData.append('period_start', new Date(Date.UTC(anchorYear, anchorMonth, 1)).toISOString());
    formData.append('period_end', new Date(Date.UTC(anchorYear, anchorMonth + 1, 1)).toISOString());
  } else if (type.recurrence === '3_month') {
    const quarterStartMonth = Math.floor(anchorMonth / 3) * 3;
    formData.append('period_start', new Date(Date.UTC(anchorYear, quarterStartMonth, 1)).toISOString());
    formData.append('period_end', new Date(Date.UTC(anchorYear, quarterStartMonth + 3, 1)).toISOString());
  }

  // Set expiry date (only append once, after period logic)
  if (type.canExpire && expiryDate) {
    formData.append('expiryDate', expiryDate);
  }

  return formData;
};

export const getRecurrenceText = (recurrence: string) => {
    switch(recurrence) {
      case "none":
        return "None";
      case "1_month":
        return "1 Month";
      case "3_month": 
        return "3 Months";
      case "1_year":
        return "1 Year";
      case "3_year":
        return "3 Years";
      default:
        return "Select Recurrence";
    }
  }